import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../../../../providers/ToastProvider';
import { DRAFT_GONE_MESSAGE, isDraftGone404 } from '../../../../services/clinicalDraftGone';
import type { ClinicalDraftFns, ClinicalDraftBody, ClinicalFormData } from './clinicalWizardConfig';
import type { TimelineEvent } from '../types';

const PENDING_DRAFT_SAVE_WAIT_MS = 5000;

interface UseClinicalDraftLifecycleParams<T extends ClinicalFormData> {
    patientId: string;
    resumingDraft?: TimelineEvent;
    drafts: ClinicalDraftFns<T>;
    /** A callback so every save reads the freshest payload without re-binding effects. */
    buildDraftBody: () => ClinicalDraftBody<T>;
    /** True when the local payload has unsaved changes vs. the initial. */
    isDirty: boolean;
    /** Step index that drives auto-save on navigation. */
    stepIndex: number;
    /** True when the wizard is on the review step (no auto-save there). */
    isOnReviewStep: boolean;
    /** True when a submit is in flight (no auto-save during submission). */
    isSubmitting: boolean;
    /** Why the backend would reject the draft; while set, every save is skipped and this is shown. */
    saveBlockedReason?: string;
    /** Called after a successful manual save so callers can signal data changes. */
    onDataChange?: () => void;
    /** Lets the work-queue TaskSurface await a save before navigating away. */
    onRegisterBeforeClose?: (handler: (() => Promise<void>) | null) => void;
}

interface UseClinicalDraftLifecycleResult {
    currentDraftId: string | null;
    currentDraftIdRef: React.MutableRefObject<string | null>;
    isSavingDraft: boolean;
    isSavingDraftSilent: boolean;
    showSavedBadge: boolean;
    /** Clear the tracked draft id (e.g. after a successful submit). */
    setCurrentDraftId: (id: string | null) => void;
    /** Manual save — loud toast on success. */
    handleSaveDraft: () => Promise<void>;
    /** Silent save — no toast, shows brief badge. Used by auto-save paths. */
    handleSaveDraftSilent: () => Promise<void>;
    /** Resolves once no draft save is in flight, so a submit sees the final draft id. */
    waitForPendingDraftSave: () => Promise<void>;
}

// SDI/RDI draft lifecycle: tracks the server draft id (in a ref, so create runs once),
// loud/silent saves, auto-save on step navigation and the brief "Saved" badge.
export function useClinicalDraftLifecycle<T extends ClinicalFormData>({
    patientId,
    resumingDraft,
    drafts,
    buildDraftBody,
    isDirty,
    stepIndex,
    isOnReviewStep,
    isSubmitting,
    saveBlockedReason,
    onDataChange,
    onRegisterBeforeClose
}: UseClinicalDraftLifecycleParams<T>): UseClinicalDraftLifecycleResult {
    const { showToast } = useToast();

    const [currentDraftId, setCurrentDraftId] = useState<string | null>(
        () => resumingDraft?.id ?? null
    );
    const currentDraftIdRef = useRef(currentDraftId);
    currentDraftIdRef.current = currentDraftId;

    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const [isSavingDraftSilent, setIsSavingDraftSilent] = useState(false);
    const [showSavedBadge, setShowSavedBadge] = useState(false);

    const savedBadgeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(
        () => () => {
            if (savedBadgeTimeoutRef.current) clearTimeout(savedBadgeTimeoutRef.current);
        },
        []
    );

    // Serialises persists so racing triggers never create two drafts: a new caller waits for
    // the in-flight one (which sets the draft id), then runs its own save as an update.
    const persistInFlightRef = useRef<Promise<void> | null>(null);

    // Set when the server reports the draft gone (submitted or discarded elsewhere): auto-save
    // disarms rather than resurrect a duplicate, and the stale id is dropped so no replay reuses it.
    const draftGoneRef = useRef(false);
    // The silent path reports a vanished draft once, not on every auto-save retry.
    const draftGoneNotifiedRef = useRef(false);

    const persistDraft = useCallback(
        async (allowRecreate = false): Promise<void> => {
            if (saveBlockedReason) throw new Error(saveBlockedReason);
            if (persistInFlightRef.current !== null) {
                // Wait for the in-flight persist to finish, then run our own.
                await persistInFlightRef.current.catch(() => undefined);
            }

            const run = (async () => {
                if (draftGoneRef.current && !allowRecreate) throw new Error(DRAFT_GONE_MESSAGE);
                const body = buildDraftBody();
                const draftId = currentDraftIdRef.current;
                if (draftId) {
                    try {
                        await drafts.updateDraft(patientId, draftId, body);
                    } catch (err) {
                        if (!isDraftGone404(err)) throw err;
                        draftGoneRef.current = true;
                        setCurrentDraftId(null);
                        currentDraftIdRef.current = null;
                        throw new Error(DRAFT_GONE_MESSAGE);
                    }
                } else {
                    const event = await drafts.createDraft(patientId, body);
                    setCurrentDraftId(event.id);
                    currentDraftIdRef.current = event.id;
                    // A fresh draft exists again: re-arm auto-save and the gone-notice.
                    draftGoneRef.current = false;
                    draftGoneNotifiedRef.current = false;
                }
            })();

            const tracked = run.finally(() => {
                persistInFlightRef.current = null;
            });
            // Only signals settlement to queued persists; swallowed here so a failure is not also
            // an unhandled rejection. The caller still gets the real error from `await run`.
            tracked.catch(() => undefined);
            persistInFlightRef.current = tracked;

            await run;
        },
        [buildDraftBody, drafts, patientId, saveBlockedReason]
    );

    const handleSaveDraft = useCallback(async (): Promise<void> => {
        setIsSavingDraft(true);
        try {
            // Explicit user action: may re-create a draft after the original vanished.
            await persistDraft(true);
            onDataChange?.();
            showToast('Draft saved.', 'success');
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Failed to save draft.';
            showToast(msg, 'error');
            throw err;
        } finally {
            setIsSavingDraft(false);
        }
    }, [persistDraft, onDataChange, showToast]);

    const handleSaveDraftSilent = useCallback(async (): Promise<void> => {
        setIsSavingDraftSilent(true);
        try {
            await persistDraft();
            if (savedBadgeTimeoutRef.current) clearTimeout(savedBadgeTimeoutRef.current);
            setShowSavedBadge(true);
            savedBadgeTimeoutRef.current = setTimeout(() => setShowSavedBadge(false), 2000);
        } catch (err) {
            // Auto-save failures stay silent, except a vanished draft: edits stopped persisting.
            const msg = err instanceof Error ? err.message : '';
            if (msg === DRAFT_GONE_MESSAGE && !draftGoneNotifiedRef.current) {
                draftGoneNotifiedRef.current = true;
                showToast(DRAFT_GONE_MESSAGE, 'error');
            }
        } finally {
            setIsSavingDraftSilent(false);
        }
    }, [persistDraft, showToast]);

    // Auto-save on step navigation; not on review (PDF build) or during a submit.
    const prevStepRef = useRef<number | undefined>(undefined);
    useEffect(() => {
        const prev = prevStepRef.current;
        if (
            prev !== undefined &&
            prev !== stepIndex &&
            isDirty &&
            !isOnReviewStep &&
            !isSubmitting
        ) {
            void handleSaveDraftSilent();
        }
        prevStepRef.current = stepIndex;
    }, [stepIndex, isDirty, isOnReviewStep, isSubmitting, handleSaveDraftSilent]);

    // Flush-before-close for a hosting surface; re-registers so it sees the latest isDirty.
    useEffect(() => {
        if (!onRegisterBeforeClose) return;
        onRegisterBeforeClose(async () => {
            if (!isDirty || isSubmitting) return;
            // Closing still proceeds; the toast says the latest edits stayed unsaved.
            if (saveBlockedReason) showToast(`Draft not saved: ${saveBlockedReason}`, 'error');
            else await handleSaveDraftSilent();
        });
        return () => onRegisterBeforeClose(null);
    }, [
        onRegisterBeforeClose,
        isDirty,
        isSubmitting,
        saveBlockedReason,
        showToast,
        handleSaveDraftSilent
    ]);

    const waitForPendingDraftSave = useCallback(async (): Promise<void> => {
        const drain = async () => {
            // A queued persist replaces the ref only after the one it waited on settles.
            while (persistInFlightRef.current !== null) {
                await persistInFlightRef.current.catch(() => undefined);
            }
        };
        let timer: ReturnType<typeof setTimeout> | undefined;
        // apiV2 has no request timeout, so a stalled save must not hold the submit forever.
        const timeout = new Promise<void>((resolve) => {
            timer = setTimeout(resolve, PENDING_DRAFT_SAVE_WAIT_MS);
        });
        await Promise.race([drain(), timeout]);
        clearTimeout(timer);
    }, []);

    return {
        currentDraftId,
        currentDraftIdRef,
        isSavingDraft,
        isSavingDraftSilent,
        showSavedBadge,
        setCurrentDraftId,
        handleSaveDraft,
        handleSaveDraftSilent,
        waitForPendingDraftSave
    };
}
